import React from 'react';
import '../../css/plantFinderStylings/PlantFinderPlantResultCardStyling.css';
import fallbackImage from '../../images/fallback/plantFallbackImage.png';

const PlantFinderPlantResultCard = ({ plant }) => {
    let numBadges = 0;
    // Helper function to format arrays
    const formatArray = (arr) => {
        if (!arr || !Array.isArray(arr)) return 'Not specified';
        return arr.join(', ');
    };

    // Helper function to get first item from array or return string
    const getFirstOrString = (value) => {
        if (Array.isArray(value)) return value[0] || 'Not specified';
        return value || 'Not specified';
    };

    return (
        <div className="plant-card">
            <div className="plant-card-image-container">
                <img
                    src={plant.optimized_plant_image_url || plant.plant_image_url || fallbackImage}
                    alt={plant.plant_name}
                    className="plant-card-image"
                    onError={(e) => {
                        e.target.src = fallbackImage;
                    }}
                />
                <div className="plant-card-badges">
                    {[
                        plant.pollinator_friendly && (
                            <span className="badge pollinator-badge" key="pollinator">
                                <span className="badge-emoji">🐝</span>
                                <span className="badge-text">Pollinator Friendly</span>
                            </span>
                        ),
                        plant.drought_tolerance === 'High' && (
                            <span className="badge drought-badge" key="drought">
                                <span className="badge-emoji">🌵</span>
                                <span className="badge-text">Drought Tolerant</span>
                            </span>
                        ),
                        plant.organic_pest_control && (
                            <span className="badge pest-control-badge" key="pest-control">
                                <span className="badge-emoji">🛡️</span>
                                <span className="badge-text">Pest Control</span>
                            </span>
                        ),
                        plant.growth_speed === 'High' && (
                            <span className="badge growth-speed-badge" key="growth-speed">
                                <span className="badge-emoji">🚀</span>
                                <span className="badge-text">Fast Grower</span>
                            </span>
                        ),
                        plant.good_for_preserving && (
                            <span className="badge good-for-preserving-badge" key="good-for-preserving">
                                <span className="badge-emoji">🫙</span>
                                <span className="badge-text">Good for Preserving</span>
                            </span>
                        ),
                        plant.pet_safe_non_toxic && (
                            <span className="badge pet-safe-non-toxic-badge" key="pet-safe-non-toxic">
                                <span className="badge-emoji">🐾</span>
                                <span className="badge-text">Pet Safe/Non-toxic</span>
                            </span>
                        ),
                        plant.continuously_harvestable && (
                            <span className="badge continuously-harvestable-badge" key="continuously-harvestable">
                                <span className="badge-emoji">🔁</span>
                                <span className="badge-text">Continuously Harvestable</span>
                            </span>
                        )
                    ]
                        .filter(Boolean) // remove false/null entries
                        .slice(0, 2)     // limit to 2 badges
                    }
                </div>

            </div>

            <div className="plant-card-content">
                <div className="plant-card-header">
                    <h3 className="plant-name">{plant.plant_name}</h3>
                    <p className="plant-scientific-name">{plant.scientific_name}</p>
                    <div className={"plant-header-tags-div"}>
                        <span className="plant-type-tag">{plant.plant_type}</span>
                        <span className="plant-tag-tag">{plant.tags[0]}</span>
                    </div>
                </div>

                <p className="plant-description">
                    {plant.short_description.substring(0, 150) + '...' || plant.long_description?.substring(0, 150) + '...' || 'No description available'}
                </p>

                <div className="plant-details-grid">
                    <div className="detail-item">
                        <span className="detail-label">Days to Maturity:</span>
                        <span className="detail-value">{plant.days_to_maturity || 'Not specified'}</span>
                    </div>

                    <div className="detail-item">
                        <span className="detail-label">Sunlight:</span>
                        <span className="detail-value">{getFirstOrString(plant.sunlight)}</span>
                    </div>

                    <div className="detail-item">
                        <span className="detail-label">Water Needs:</span>
                        <span className="detail-value">{plant.water_needs || 'Not specified'}</span>
                    </div>

                    <div className="detail-item">
                        <span className="detail-label">Planting Season:</span>
                        <span className="detail-value">{formatArray(plant.planting_season)}</span>
                    </div>

                    <div className="detail-item">
                        <span className="detail-label">Mature Size:</span>
                        <span className="detail-value">
                            {plant.mature_height && plant.mature_width
                                ? `${plant.mature_height} × ${plant.mature_width}`
                                : plant.mature_height || plant.mature_width || 'Not specified'
                            }
                        </span>
                    </div>

                    <div className="detail-item">
                        <span className="detail-label">Hardiness Zones:</span>
                        <span className="detail-value">{plant.hardiness_range}</span>
                    </div>
                </div>

                {plant.companion_plants && plant.companion_plants.length > 0 && (
                    <div className="companion-plants">
                        <h4>Good Companions:</h4>
                        <div className="companion-tags">
                            {plant.companion_plants.slice(0, 4).map((companion, index) => (
                                <span key={index} className="companion-tag">{companion}</span>
                            ))}
                            {plant.companion_plants.length > 4 && (
                                <span className="companion-tag more">+{plant.companion_plants.length - 4} more</span>
                            )}
                        </div>
                    </div>
                )}

                <div className={`plant-finder-card-bottom-buttons`}>
                    <button className={`bottom-button-view-more-button`}>
                        View More
                    </button>

                    {plant.amazon_purchase_link && (
                        <div className="plant-card-actions">
                            <a
                                href={plant.amazon_purchase_link}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="purchase-link"
                            >
                                View on Amazon
                            </a>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default PlantFinderPlantResultCard;